import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteValue,
  type ChipTypeMap,
  TextField,
} from "@mui/material";
import { Controller, type ControllerProps, type FieldPath, type FieldValues } from "react-hook-form";

export interface IAutocompleteForm<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> extends Omit<ControllerProps<TFieldValues, TName, TTransformedValues>, "render">,
    Omit<
      AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
      "defaultValue" | "value" | "onChange" | "onBlur" | "disabled" | "renderInput"
    > {
  label?: string;
  transformValue?: (
    value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>
  ) => AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;
}

export const AutocompleteForm = <
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  name,
  control,
  defaultValue,
  disabled,
  rules,
  shouldUnregister,
  label,
  transformValue,
  ...props
}: IAutocompleteForm<
  Value,
  Multiple,
  DisableClearable,
  FreeSolo,
  ChipComponent,
  TFieldValues,
  TName,
  TTransformedValues
>) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...props}
          value={field.value}
          onChange={(_, value) => {
            const transformedValue = transformValue ? transformValue(value) : value;
            field.onChange(transformedValue);
          }}
          onBlur={field.onBlur}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              name={field.name}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputRef={field.ref}
            />
          )}
        />
      )}
    />
  );
};
